import { useTranslation } from 'react-i18next'
import { RCSCardContent, RCSContent, RCSSuggestion, TemplateUpdateParams } from '../../../../types'
import { useCallback } from 'react'
import { Alert, Button, Heading, Tabs } from '../../../../ui'
import { UseFormReturn } from 'react-hook-form'
import ImageField from '../../../../ui/form/ImageField'
import TextInput from '../../../../ui/form/TextInput'
import VariableTextInput from '../../../../ui/form/VariableTextInput'
import { SingleSelect } from '../../../../ui/form/SingleSelect'
import './SuggestionEditor.css'

const suggestionTypes = [
    { key: 'reply' as const, label: 'Quick Reply' },
    { key: 'url' as const, label: 'Open URL' },
    { key: 'dial' as const, label: 'Dial Phone' },
    { key: 'location' as const, label: 'Show Location' },
]

const mediaHeightOptions: Array<{ key: 'SHORT' | 'MEDIUM' | 'TALL', label: string }> = [
    { key: 'SHORT', label: 'Short' },
    { key: 'MEDIUM', label: 'Medium' },
    { key: 'TALL', label: 'Tall' },
]

const cardOrientationOptions: Array<{ key: 'VERTICAL' | 'HORIZONTAL', label: string }> = [
    { key: 'VERTICAL', label: 'Vertical' },
    { key: 'HORIZONTAL', label: 'Horizontal' },
]

function emptySuggestion(): RCSSuggestion {
    return { type: 'reply', text: '', postback_data: '' }
}

function emptyCardContent(): RCSCardContent {
    return {
        title: '',
        description: '',
        media: { file_url: '', height: 'MEDIUM' },
    }
}

function SuggestionRow({ value, onChange, onRemove, index }: {
    value: RCSSuggestion
    onChange: (s: RCSSuggestion) => void
    onRemove: () => void
    index: number
}) {
    const { t } = useTranslation()

    const handleTypeChange = (type: string) => {
        if (type === 'reply') {
            onChange({ type: 'reply', text: value.text, postback_data: '' })
        } else if (type === 'url') {
            onChange({ type: 'url', text: value.text, url: '' })
        } else if (type === 'dial') {
            onChange({ type: 'dial', text: value.text, phone_number: '' })
        } else if (type === 'location') {
            onChange({ type: 'location', text: value.text, latitude: 0, longitude: 0 })
        }
    }

    return (
        <div className="suggestion-row">
            <div className="suggestion-row-fields">
                <SingleSelect
                    label={t('rcs_action_type')}
                    options={suggestionTypes}
                    value={value.type}
                    onChange={handleTypeChange}
                    toValue={(o) => o.key}
                    getValueKey={(v) => v}
                    getOptionDisplay={(o) => o.label}
                />
                <VariableTextInput
                    name={`suggestion_${index}_text`}
                    label={t('rcs_button_label')}
                    value={value.text}
                    onChange={(text: string) => {
                        const updated = { ...value, text }
                        onChange(updated)
                    }}
                />
                {value.type === 'url' && (
                    <VariableTextInput
                        name={`suggestion_${index}_url`}
                        label={t('url')}
                        value={value.url}
                        onChange={(url: string) => onChange({ ...value, url })}
                    />
                )}
                {value.type === 'dial' && (
                    <TextInput
                        name={`suggestion_${index}_phone`}
                        label={t('phone')}
                        value={value.phone_number}
                        onChange={(phone_number: string) => onChange({ ...value, phone_number })}
                    />
                )}
                {value.type === 'location' && (
                    <>
                        <TextInput
                            name={`suggestion_${index}_lat`}
                            label={t('rcs_latitude')}
                            value={String(value.latitude)}
                            onChange={(v: string) => onChange({ ...value, latitude: parseFloat(v) || 0 })}
                        />
                        <TextInput
                            name={`suggestion_${index}_lng`}
                            label={t('rcs_longitude')}
                            value={String(value.longitude)}
                            onChange={(v: string) => onChange({ ...value, longitude: parseFloat(v) || 0 })}
                        />
                        <TextInput
                            name={`suggestion_${index}_label`}
                            label={t('label')}
                            value={value.label ?? ''}
                            onChange={(label: string) => onChange({ ...value, label })}
                        />
                    </>
                )}
            </div>
            <div className="suggestion-row-footer">
                <Button variant="destructive_secondary" size="small" onClick={onRemove}>
                    {t('remove')}
                </Button>
            </div>
        </div>
    )
}

function SuggestionsEditor({ suggestions, onChange }: {
    suggestions: RCSSuggestion[]
    onChange: (suggestions: RCSSuggestion[]) => void
}) {
    const { t } = useTranslation()

    return (
        <div>
            <Heading title={t('text_interactive_buttons')} size="h4" actions={
                <Button
                    variant="secondary"
                    size="small"
                    onClick={() => onChange([...suggestions, emptySuggestion()])}
                >
                    {t('text_interactive_add_button')}
                </Button>
            } />
            {suggestions.length === 0
                ? <Alert variant="plain" title={t('text_interactive_buttons_none')} body={t('text_interactive_buttons_empty')} />
                : <div className="suggestions-list">
                    {suggestions.map((s, i) => (
                        <SuggestionRow
                            key={i}
                            value={s}
                            index={i}
                            onChange={(updated) => {
                                const next = [...suggestions]
                                next[i] = updated
                                onChange(next)
                            }}
                            onRemove={() => {
                                onChange(suggestions.filter((_, idx) => idx !== i))
                            }}
                        />
                    ))}
                </div>
            }
        </div>
    )
}

function CardEditor({ form }: { form: UseFormReturn<TemplateUpdateParams, any> }) {
    const { t } = useTranslation()
    const rcs: RCSContent | undefined = form.watch('data.rcs' as any)
    const richCard = rcs?.rich_card
    const card = richCard?.card_content ?? emptyCardContent()

    const updateRichCard = useCallback((updates: { card_orientation?: 'VERTICAL' | 'HORIZONTAL', card_content?: Partial<RCSCardContent> }) => {
        const current = form.getValues('data.rcs' as any) as RCSContent
        const currentCard = current.rich_card
        form.setValue('data.rcs' as any, {
            ...current,
            rich_card: {
                ...currentCard,
                card_orientation: updates.card_orientation ?? currentCard?.card_orientation ?? 'VERTICAL',
                card_content: {
                    ...(currentCard?.card_content ?? emptyCardContent()),
                    ...updates.card_content,
                },
            },
        } as any, { shouldDirty: true })
    }, [form])

    return (
        <div>
            <SingleSelect
                label={t('text_interactive_card_orientation')}
                options={cardOrientationOptions}
                value={richCard?.card_orientation ?? 'VERTICAL'}
                onChange={(v) => updateRichCard({ card_orientation: v })}
                toValue={(o) => o.key}
                getValueKey={(v) => v}
                getOptionDisplay={(o) => o.label}
            />
            <VariableTextInput
                name="card_title"
                label={t('title')}
                value={card.title ?? ''}
                onChange={(title: string) => updateRichCard({ card_content: { title } })}
                required
            />
            <VariableTextInput
                name="card_description"
                label={t('description')}
                value={card.description ?? ''}
                onChange={(description: string) => updateRichCard({ card_content: { description } })}
                multiline
            />
            <ImageField
                form={form}
                name={'data.rcs.rich_card.card_content.media.file_url' as any}
                label={t('text_interactive_image')}
            />
            <SingleSelect
                label={t('rcs_media_height')}
                options={mediaHeightOptions}
                value={card.media?.height ?? 'MEDIUM'}
                onChange={(height) => updateRichCard({
                    card_content: { media: { ...card.media, file_url: card.media?.file_url ?? '', height } },
                })}
                toValue={(o) => o.key}
                getValueKey={(v) => v}
                getOptionDisplay={(o) => o.label}
            />
        </div>
    )
}

export const TextForm = ({ form }: { form: UseFormReturn<TemplateUpdateParams, any> }) => {
    const { t } = useTranslation()
    const editor = form.watch('data.editor' as any) as string | undefined
    const isInteractive = editor === 'interactive'
    const rcs: RCSContent | undefined = form.watch('data.rcs' as any)

    const updateSuggestions = useCallback((suggestions: RCSSuggestion[]) => {
        const current = form.getValues('data.rcs' as any) as RCSContent | undefined
        form.setValue('data.rcs' as any, {
            ...current,
            mode: current?.mode ?? 'rich_card',
            suggestions,
        } as any, { shouldDirty: true })
    }, [form])

    if (!isInteractive) {
        return (
            <>
                <Heading title={t('message')} size="h4" />
                <VariableTextInput.Field
                    form={form}
                    name="data.text"
                    label={t('message')}
                    multiline
                    required />
                <Heading title={t('advanced')} size="h4" />
                <VariableTextInput.Field
                    form={form}
                    name="data.media_url"
                    label={t('media_url')}
                    subtitle={t('media_url_desc')} />
            </>
        )
    }

    return <Tabs tabs={[
        {
            key: 'message',
            label: t('message'),
            children: <CardEditor form={form} />,
        },
        {
            key: 'buttons',
            label: t('text_interactive_buttons'),
            children: (
                <SuggestionsEditor
                    suggestions={rcs?.suggestions ?? []}
                    onChange={updateSuggestions}
                />
            ),
        },
        {
            key: 'fallback',
            label: t('fallback'),
            children: (
                <>
                    <Heading title={t('text_fallback')} size="h4" />
                    <VariableTextInput.Field
                        form={form}
                        name="data.text"
                        label={t('text_fallback')}
                        subtitle={t('text_interactive_fallback_desc')}
                        multiline
                        required />
                </>
            ),
        },
    ]} />
}
